import { useMemo } from "react";
import { ArrowRight, Home, MapPin, Search } from "lucide-react";
import useSWR from "swr";

import { formatDisplayDate } from "../../../../utils/Date";
import type { HighCourtRow } from "./types";
import {
  EmptyRow,
  IconBox,
  LoadingRow,
  MobileCardList,
  MobileInfoRow,
  PageTop,
  ResponsiveTableShell,
  RowActions,
} from "./SharedUi";

interface HighCourtsProps {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onBack: () => void;
  onAdd: () => void;
  onOpen: (court: HighCourtRow) => void;
  onEdit: (court: HighCourtRow) => void;
  onDelete: (court: HighCourtRow) => void;
  loading?: boolean;
}

export const HighCourts = ({
  searchTerm,
  onSearchTermChange,
  onBack,
  onAdd,
  onOpen,
  onEdit,
  onDelete,
}: HighCourtsProps) => {
  const { data: highCourtResponse, isLoading } = useSWR(
    "/api/court/get-highcourt",
  );
  const highCourts = (highCourtResponse?.data || []) as HighCourtRow[];
  const courts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return highCourts;

    return highCourts.filter((court) =>
      [court.highCourtName, court.stateName, court.stateCode, court.capitalCity]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term)),
    );
  }, [highCourts, searchTerm]);

  return (
    <div>
      <PageTop
        backLabel="Back to Courts"
        title="High Courts"
        count={`${courts.length} court${courts.length === 1 ? "" : "s"}`}
        subtitle="Click on a court name to manage its benches and court halls"
        actionLabel="Add High Court"
        onBack={onBack}
        onAction={onAdd}
      />

      <div className="mb-5 flex gap-3">
        <div className="flex h-12 flex-1 items-center gap-3 rounded-lg border border-slate-200 bg-white px-4">
          <Search className="h-5 w-5 text-slate-400" />
          <input
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
            placeholder="Search high courts..."
            className="h-full flex-1 bg-transparent outline-none"
          />
        </div>
      </div>

      <ResponsiveTableShell>
        <table className="w-full min-w-[950px]">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-6 py-4">High Court Name</th>
              <th className="px-6 py-4">State</th>
              <th className="px-6 py-4">Code</th>
              <th className="px-6 py-4">Established</th>
              <th className="px-6 py-4">Capital City</th>
              <th className="px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && <LoadingRow colSpan={6} />}
            {courts?.length === 0 ? (
              <EmptyRow
                colSpan={6}
                label="Add more high courts to this list"
                actionLabel="Add High Court"
                onAction={onAdd}
              />
            ) : (
              courts?.map((court) => (
                <tr key={court._id} className="hover:bg-slate-50">
                  <td className="px-6 py-5">
                    <button
                      type="button"
                      onClick={() => onOpen(court)}
                      className="flex items-center gap-4 text-left"
                    >
                      <IconBox icon={<Home size={24} />} />
                      <span>
                        <span className="block font-semibold text-blue-700">
                          {court.highCourtName}
                          <ArrowRight className="ml-2 inline h-4 w-4" />
                        </span>
                        <span className="text-sm text-slate-400">
                          Click to manage benches
                        </span>
                      </span>
                    </button>
                  </td>
                  <td className="px-6 py-5">{court.stateName}</td>
                  <td className="px-6 py-5">
                    <span className="rounded bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                      {court.stateCode}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    {formatDisplayDate(court.established)}
                  </td>
                  <td className="px-6 py-5">
                    <span className="inline-flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      {court.capitalCity}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <RowActions
                      onEdit={() => onEdit(court)}
                      onDelete={() => onDelete(court)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </ResponsiveTableShell>

      <MobileCardList
        isLoading={isLoading}
        isEmpty={courts.length === 0}
        emptyLabel="Add more high courts to this list"
        actionLabel="Add High Court"
        onAction={onAdd}
      >
        {courts.map((court) => (
          <article
            key={court._id}
            className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <button
                type="button"
                onClick={() => onOpen(court)}
                className="flex min-w-0 items-start gap-3 text-left"
              >
                <IconBox icon={<Home size={22} />} />
                <span className="min-w-0">
                  <span className="block font-semibold text-blue-700">
                    {court.highCourtName}
                    <ArrowRight className="ml-1 inline h-4 w-4" />
                  </span>
                  <span className="text-sm text-slate-400">
                    Click to manage benches
                  </span>
                </span>
              </button>
              <RowActions
                onEdit={() => onEdit(court)}
                onDelete={() => onDelete(court)}
              />
            </div>
            <div className="mt-4 space-y-3">
              <MobileInfoRow label="State" value={court.stateName || "N/A"} />
              <MobileInfoRow
                label="Code"
                value={
                  <span className="rounded bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700">
                    {court.stateCode || "N/A"}
                  </span>
                }
              />
              <MobileInfoRow
                label="Established"
                value={formatDisplayDate(court.established)}
              />
              <MobileInfoRow
                label="Capital City"
                value={
                  <span className="inline-flex items-center justify-end gap-2">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    {court.capitalCity || "N/A"}
                  </span>
                }
              />
            </div>
          </article>
        ))}
      </MobileCardList>
    </div>
  );
};
